import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Pause, RotateCcw, Timer } from 'lucide-react';

const ROTATION_SECONDS = 5 * 60; // 5 minutes
const TICK_MS = 1000;

const formatTime = (totalSeconds) => {
  const safe = Math.max(0, totalSeconds | 0);
  const mm = String(Math.floor(safe / 60)).padStart(2, '0');
  const ss = String(safe % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const sortByJoinedAt = (arr) =>
  [...arr].sort((a, b) => new Date(a.joined_at) - new Date(b.joined_at));

const playWhistle = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = 500;
    gain.gain.value = 0.15;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    setTimeout(() => {
      try {
        osc.stop();
        ctx.close();
      } catch (e) {
        // ignore
      }
    }, 200);
  } catch (e) {
    // audio not available, ignore
  }
  try {
    if (navigator.vibrate) navigator.vibrate(200);
  } catch (e) {
    // ignore
  }
};

const TeamColumn = ({ label, accent, players, currentIndex }) => {
  const accentClasses = accent === 'blue'
    ? 'from-blue-500/10 via-blue-500/5 to-transparent border-blue-500/20 text-blue-700 dark:text-blue-300'
    : 'from-rose-500/10 via-rose-500/5 to-transparent border-rose-500/20 text-rose-700 dark:text-rose-300';
  const dotClass = accent === 'blue' ? 'bg-blue-500' : 'bg-rose-500';

  if (!players.length) {
    return (
      <div className={`rounded-xl bg-gradient-to-br border p-3 ${accentClasses}`}>
        <h4 className="font-bold mb-2 flex items-center gap-2 text-sm uppercase tracking-widest">
          <span className={`inline-block h-2 w-2 rounded-full ${dotClass}`} />
          {label}
        </h4>
        <p className="text-xs text-muted-foreground">Nessun giocatore confermato.</p>
      </div>
    );
  }

  const safeIndex = ((currentIndex % players.length) + players.length) % players.length;

  return (
    <div className={`rounded-xl bg-gradient-to-br border p-3 ${accentClasses}`}>
      <h4 className="font-bold mb-3 flex items-center gap-2 text-sm uppercase tracking-widest">
        <span className={`inline-block h-2 w-2 rounded-full ${dotClass}`} />
        {label}
        <span className="text-muted-foreground font-normal tabular-nums">({players.length})</span>
      </h4>
      <ol className="space-y-1.5">
        {players.map((p, idx) => {
          const isCurrent = idx === safeIndex;
          const name = p.user?.name || p.user?.email || 'Giocatore';
          return (
            <li
              key={p.participant_id || p.user_id || idx}
              className={`flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm ${
                isCurrent ? 'bg-yellow-400/20 ring-1 ring-yellow-500/40 font-semibold' : ''
              }`}
              data-testid={isCurrent ? 'goalkeeper-current' : undefined}
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className="tabular-nums text-xs text-muted-foreground w-5 text-right">
                  {idx + 1}.
                </span>
                <span className="truncate">{name}</span>
              </span>
              {isCurrent && (
                <Badge variant="secondary" className="shrink-0 bg-yellow-500/20 text-yellow-800 dark:text-yellow-200 border-yellow-500/30">
                  🧤 In porta
                </Badge>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const GoalkeeperRotationPanel = ({ teamA = [], teamB = [] }) => {
  const orderedA = useMemo(() => sortByJoinedAt(teamA), [teamA]);
  const orderedB = useMemo(() => sortByJoinedAt(teamB), [teamB]);

  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(ROTATION_SECONDS);
  const [keeperIndex, setKeeperIndex] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!running) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          playWhistle();
          setKeeperIndex((idx) => idx + 1);
          return ROTATION_SECONDS;
        }
        return prev - 1;
      });
    }, TICK_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [running]);

  useEffect(() => () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  const handleStart = () => setRunning(true);
  const handlePause = () => setRunning(false);
  const handleReset = () => {
    setRunning(false);
    setRemaining(ROTATION_SECONDS);
    setKeeperIndex(0);
  };

  const hasAnyone = orderedA.length > 0 || orderedB.length > 0;

  return (
    <Card data-testid="goalkeeper-rotation-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Timer className="h-5 w-5 text-primary" />
          Rotazione portiere
          <span className="text-sm font-normal text-muted-foreground">
            ogni 5 min
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl bg-muted">
          <div className="flex items-center gap-3">
            <div
              className="text-4xl sm:text-5xl font-bold tabular-nums tracking-tight"
              data-testid="goalkeeper-rotation-timer"
            >
              {formatTime(remaining)}
            </div>
            <span className="text-xs text-muted-foreground max-w-[160px]">
              Cambio portiere quando il timer raggiunge 0:00
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {!running ? (
              <Button
                type="button"
                onClick={handleStart}
                disabled={!hasAnyone}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="goalkeeper-rotation-start"
              >
                <Play className="h-4 w-4 mr-2" />
                Avvia
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handlePause}
                variant="outline"
                data-testid="goalkeeper-rotation-pause"
              >
                <Pause className="h-4 w-4 mr-2" />
                Pausa
              </Button>
            )}
            <Button
              type="button"
              onClick={handleReset}
              variant="outline"
              data-testid="goalkeeper-rotation-reset"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TeamColumn label="Team A" accent="blue" players={orderedA} currentIndex={keeperIndex} />
          <TeamColumn label="Team B" accent="rose" players={orderedB} currentIndex={keeperIndex} />
        </div>

        {!hasAnyone && (
          <p className="text-xs text-muted-foreground">
            Aggiungi giocatori confermati alle squadre per attivare la rotazione.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default GoalkeeperRotationPanel;
